import type { IOmit } from '@sixui/ui';
import { Button, Dialog, IDialogProps, useId } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { WPPlaylistSelector } from '~/components/organisms/fields/WPPlaylistSelector';

export type IWPPlaylistMdxDirectiveDialogFormValues = {
  url?: string;
};

export type IWPPlaylistMdxDirectiveDialogProps = IOmit<
  IDialogProps,
  'headline' | 'actions' | 'children'
> & {
  appId: string;
  onSubmit?: (
    values: IWPPlaylistMdxDirectiveDialogFormValues,
    event?: React.BaseSyntheticEvent,
  ) => void;
  defaultValues?: IWPPlaylistMdxDirectiveDialogFormValues;
};

const styles = stylex.create({
  host: {
    minWidth: 480,
  },
});

export const WPPlaylistMdxDirectiveDialogForm: React.FC<
  IWPPlaylistMdxDirectiveDialogProps
> = (props) => {
  const { sx, appId, onSubmit, defaultValues, ...other } = props;
  const { t } = useTranslation();
  const form = useForm<IWPPlaylistMdxDirectiveDialogFormValues>({
    defaultValues,
  });
  const formId = useId();

  return (
    <Dialog
      {...other}
      sx={[styles.host, sx]}
      headline={t('WPPlaylistMdxDirectiveDialogForm.title')}
      actions={({ close }) => (
        <>
          <Button
            variant="text"
            onClick={close}
            disabled={form.formState.isSubmitting}
            form={formId}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onSubmit ? form.handleSubmit(onSubmit) : undefined}
            form={formId}
          >
            {t('common.insert')}
          </Button>
        </>
      )}
    >
      <ContentBlock.FormBody
        id={formId}
        form={form}
        onSubmit={onSubmit}
        hideActionBar={true}
        extended
      >
        <FormSection>
          <FormField
            as={WPPlaylistSelector}
            forwardError
            control={form.control}
            name="url"
            rules={{ required: true }}
            appId={appId}
          />
        </FormSection>
      </ContentBlock.FormBody>
    </Dialog>
  );
};
